/**
 * Permission Hooks
 *
 * Centralised, react-admin-aware role checks. Mirrors the Notify Angular
 * reference's per-page gating rules exactly, expressed as composable hooks:
 *
 *   - `useIsSuperUser()`   — true iff the current user is SuperUser.
 *   - `useIsMaintenance()` — true iff the current user is Maintenance.
 *   - `useCanCreate()`     — Notify Angular: `*ngIf="userRole != 'Maintenance'"`.
 *                            Hides "Add"/"Create" toolbar buttons from
 *                            Maintenance users.
 *   - `useCanEdit()`       — Notify Angular: action column rendered only when
 *                            `authService.checkUserRole()` returns true
 *                            (SuperUser-only). Same rule for edit buttons.
 *   - `useCanDelete()`     — same as `useCanEdit()`. Delete is a SuperUser-only
 *                            operation in the reference.
 *
 * All hooks return `false` while the permission is still loading, so any
 * gated UI is hidden by default until the SSO bootstrap finishes. This
 * matches the Angular APP_INITIALIZER guarantee that no component renders
 * before `userRole` has been resolved from the token.
 */

import { usePermissions } from 'react-admin';
import { UserRoleEnum } from '@/types/auth';

export const SUPER_USER_ROLE: string = UserRoleEnum[UserRoleEnum.SuperUser];
export const MAINTENANCE_ROLE: string = UserRoleEnum[UserRoleEnum.Maintenance];

const useRole = (): string | null => {
  const { permissions, isLoading } = usePermissions<string>();
  if (isLoading) return null;
  return permissions ?? null;
};

export const useIsSuperUser = (): boolean => useRole() === SUPER_USER_ROLE;

export const useIsMaintenance = (): boolean => useRole() === MAINTENANCE_ROLE;

/**
 * Notify Angular: every list page gates the toolbar Add button with
 * `*ngIf="userRole != 'Maintenance'"`. SuperUser sees it; Maintenance does
 * not. Anyone who hasn't loaded yet sees nothing.
 */
export const useCanCreate = (): boolean => {
  const role = useRole();
  return role !== null && role !== MAINTENANCE_ROLE;
};

/**
 * Notify Angular renders the row-action column only when
 * `AuthService.checkUserRole()` returns true — i.e. SuperUser only. Edit
 * and Delete share the same gate because both live inside that column.
 */
export const useCanEdit = (): boolean => useIsSuperUser();

export const useCanDelete = (): boolean => useIsSuperUser();

/**
 * Gate del modulo de cuentas. Es la UNICA excepcion a la regla general de
 * arriba: Maintenance gestiona cuentas de punta a punta (crear, editar,
 * bloquear, borrar, api-keys y usuarios SSO de la cuenta) porque soporte da
 * de alta clientes sin depender de un SuperUser. El backend abre los mismos
 * endpoints a `SuperUser, Maintenance` (admin.api 1.0.79).
 *
 * Deliberadamente NO se cambian `useCanCreate` / `useCanEdit` / `useCanDelete`:
 * eso abriria tambien proveedores, licencias, aplicaciones, tarifas y grupos,
 * que siguen siendo SuperUser.
 */
export const useCanManageAccounts = (): boolean => {
  const role = useRole();
  return role === SUPER_USER_ROLE || role === MAINTENANCE_ROLE;
};

/**
 * Special user-management row rules from `crud.component.ts`:
 *   - Delete is hidden when the row's role is `Maintenance`
 *     (`if (full?.role?.name.toLowerCase() != 'maintenance') push delete`).
 *   - All actions are hidden when the row's role is `SuperUser`
 *     (`if (module == 'user' && row.role == 'superuser') buttons = []`).
 *
 * These helpers take the row's role name (whatever casing the backend
 * returns) and return whether the corresponding action should be shown,
 * assuming the viewer already passed `useCanEdit()` / `useCanDelete()`.
 */
const normalize = (rowRoleName: string | null | undefined): string =>
  (rowRoleName ?? '').toLowerCase();

export const canEditUserRow = (rowRoleName: string | null | undefined): boolean =>
  normalize(rowRoleName) !== 'superuser';

export const canDeleteUserRow = (rowRoleName: string | null | undefined): boolean => {
  const r = normalize(rowRoleName);
  return r !== 'superuser' && r !== 'maintenance';
};
